import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
} from "@mui/material";
import {
  FileUpload as FileUploadIcon,
  Google as GoogleIcon,
  VpnKey as VpnKeyIcon,
} from "@mui/icons-material";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@context/NotificationContext";
import {
  stashPendingKeyImport,
  consumePendingKeyImport,
} from "@hooks/useLocalKeys";
import { parseKeyBundleJson } from "@/utils/keyBundle";
import { routes } from "@/constants/routes";

export default function ImportKeys() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, loginWithGoogle } = useAuth();
  const { notify } = useNotification();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [stashed, setStashed] = useState(false);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      void (async () => {
        const text = reader.result as string;
        const bundle = parseKeyBundleJson(text);
        if (!bundle) {
          notify(t("importKeys.invalidFile"), "error");
          return;
        }
        stashPendingKeyImport(text);
        setStashed(true);
        if (isAuthenticated) {
          const count = await consumePendingKeyImport();
          if (count > 0) {
            notify(t("importKeys.applied", { count }), "success");
          } else {
            notify(t("importKeys.stashed"), "info");
          }
          navigate(routes.dashboard, { replace: true });
        } else {
          notify(t("importKeys.stashed"), "info");
        }
      })();
    };
    reader.readAsText(file);
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        maxWidth: 560,
        mx: "auto",
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 700,
        }}
      >
        {t("importKeys.title")}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 3,
        }}
      >
        {t("importKeys.subtitle")}
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <VpnKeyIcon color="primary" />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
              }}
            >
              {t("importKeys.chooseFile")}
            </Typography>
          </Box>
          <input
            ref={inputRef}
            type="file"
            accept=".json,application/json"
            hidden
            onChange={(e) => {
              handleFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={() => inputRef.current?.click()}
            fullWidth
            sx={{ mb: 2 }}
          >
            {t("importKeys.browse")}
          </Button>
          {stashed && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t("importKeys.nextStep")}
            </Alert>
          )}
          {!isAuthenticated && (
            <Button
              variant="contained"
              size="large"
              startIcon={<GoogleIcon />}
              onClick={loginWithGoogle}
              fullWidth
            >
              {t("welcome.signIn")}
            </Button>
          )}
          {isAuthenticated && (
            <Button
              variant="contained"
              onClick={() => navigate(routes.dashboard)}
              fullWidth
            >
              {t("importKeys.goDashboard")}
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
